import React, {useCallback, useEffect, useState} from "react";
import {fabric} from "fabric";
import {FabricJSCanvas, useFabricJSEditor} from "fabricjs-react";
import {FileUploader} from "react-drag-drop-files";
import "./PhotoEditor.css";
import {CardContent, Paper} from "@mui/material";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import {ClearOutlined, DeleteOutline, Redo, StraightenOutlined, Undo} from "@mui/icons-material";
import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import PropTypes from "prop-types";
import img from './background.png'

let historyProcessing = false;

export default function PhotoEditor({closeEditor, fileTypes, size}) {
    const scaleBySize = {
        'A3': 0.1,
        'A4': 0.1414213
    };

    const [disableSubmit, setDisableSubmit] = useState(1);
    const {t} = useTranslation();
    let [isRulerVisible, setRuler] = useState(false);
    const {editor, onReady} = useFabricJSEditor();

    const [undoHistory, setUndoHistory] = useState([]);
    const [redoHistory, setRedoHistory] = useState([]);

    useEffect(() => {
        if (!editor || !fabric) {
            return;
        }

        editor.canvas.on('object:modified', updateModifications);
        editor.canvas.on('object:added', updateModifications);
        editor.canvas.on('object:removed', updateModifications);

        // Push initial state to undoHistory when editor is ready
        setUndoHistory([JSON.stringify(editor.canvas.toJSON(['id']))]);
        setDisableSubmit(!editor?.canvas.getObjects().filter(item => item.id !== 'ruler').length);
    }, [editor?.canvas, fabric]);


    useEffect(() => {
        if (!editor || !fabric) {
            return;
        }

        editor.canvas.setWidth(351);
        editor.canvas.setHeight(496);
        editor.canvas.setBackgroundImage(img, () => editor.canvas.renderAll());
        editor.canvas.renderAll();
    }, [editor?.canvas.backgroundImage]);

    const updateModifications = useCallback(() => {
        if (historyProcessing) return;
        let myjson = JSON.stringify(editor.canvas.toJSON(['id']));
        setRedoHistory([]); // Clear redo history when a new modification occurs
        setUndoHistory((prevHistory) => {
            return [...prevHistory, myjson].slice(-11);
        }); // Add new state to undoHistory
    }, [editor?.canvas]);

    function getRuler(newState) {
        const canvas = JSON.parse(newState);
        let isRulerVisible = Boolean((canvas.objects || []).filter(item => item.id === 'ruler').length);
        setRuler(isRulerVisible);
    }

    const undo = () => {
        if (undoHistory.length > 1) { // Make sure there is at least one previous state
            historyProcessing = true;
            let lastState = undoHistory[undoHistory.length - 1]; // Pop last state
            let newState = undoHistory[undoHistory.length - 2]; // Get current state
            setUndoHistory(undoHistory.slice(0, -1)); // Remove last state from undoHistory
            setRedoHistory((prevHistory) => [...prevHistory, lastState]); // Push it to redoHistory
            getRuler(newState);
            editor.canvas.loadFromJSON(newState, () => {
                editor.canvas.renderAll();
                historyProcessing = false;
                setDisableSubmit(!editor?.canvas.getObjects().filter(item => item.id !== 'ruler').length);
            });
        }
    };

    const redo = () => {
        if (redoHistory.length > 0) {
            historyProcessing = true;
            let newState = redoHistory[redoHistory.length - 1]; // Get the last redo state
            setRedoHistory(redoHistory.slice(0, -1)); // Remove it from redoHistory
            setUndoHistory((prevHistory) => [...prevHistory, newState]); // Add it back to undoHistory
            getRuler(newState);
            editor.canvas.loadFromJSON(newState, () => {
                editor.canvas.renderAll();
                historyProcessing = false;
                setDisableSubmit(!editor?.canvas.getObjects().filter(item => item.id !== 'ruler').length);
            });
        }
    };

    const clear = () => {
        editor.canvas._objects.splice(0, editor.canvas._objects.length);
        editor.canvas.discardActiveObject();
        editor.canvas.renderAll();
        setRuler(false);
        updateModifications();
        setDisableSubmit(!editor?.canvas.getObjects().filter(item => item.id !== 'ruler').length);
    };

    const removeSelectedObject = () => {
        const activeObject = editor.canvas.getActiveObject();
        if (activeObject.id === 'ruler') {
            setRuler(false);
        }

        editor.canvas.remove(activeObject);
        setDisableSubmit(!editor?.canvas.getObjects().filter(item => item.id !== 'ruler').length);
    };

    const exportSVG = () => {
        isRulerVisible && toggleRuler();
        editor.canvas.setBackgroundImage(null);
        return editor.canvas.toSVG();
    };

    const toggleRuler = () => {
        if (isRulerVisible) {
            editor.canvas.remove(editor.canvas.getObjects().filter(item => item.id === 'ruler')[0]);
            setRuler(null);
        } else {
            let group = [];
            fabric.loadSVGFromURL("/ruler.svg", function (objects, options) {
                var loadedObjects = new fabric.Group(group);
                loadedObjects.scaleToWidth(loadedObjects.width * (scaleBySize[size] || 0.1));
                loadedObjects.scaleToHeight(loadedObjects.height * (scaleBySize[size] || 0.1));
                loadedObjects.id = 'ruler';
                loadedObjects.set({
                    left: 10,
                    top: 10
                });
                loadedObjects.setControlsVisibility({
                    mt: false,
                    mr: false,
                    ml: false,
                    mb: false,
                    bl: false,
                    br: false,
                    tl: false,
                    tr: false
                });
                setRuler(true);
                editor.canvas.add(loadedObjects)
                editor.canvas.setActiveObject(loadedObjects);
                editor.canvas.renderAll();

            }, function (item, object) {
                object.set('id', item.getAttribute('id'));
                group.push(object);
            });
        }
    }

    const loadSvg = (imgObj) => {
        let reader = new FileReader();
        reader.readAsDataURL(imgObj);
        reader.onload = (e) => {
            let imageURL = e.target.result;
            let group = [];
            fabric.loadSVGFromURL(imageURL, function (objects, options) {
                var svg = new fabric.Group(group);
                svg.set({
                    left: 10,
                    top: 10
                });
                svg.scaleToHeight(svg.height * (scaleBySize[size] || 0.1));
                svg.scaleToWidth(svg.width * (scaleBySize[size] || 0.1));
                editor.canvas.add(svg)
                editor.canvas.setActiveObject(svg);
                setDisableSubmit(!editor?.canvas.getObjects().filter(item => item.id !== 'ruler').length);
            }, function (item, object) {
                object.set('id', item.getAttribute('id'));
                group.push(object);
            });
        }
    }

    const onAddImage = (imgObj) => {
        if (imgObj.type?.indexOf('svg') > -1) {
            loadSvg(imgObj);
        } else {
            let reader = new FileReader();
            reader.readAsDataURL(imgObj);
            reader.onload = (e) => {
                let imageURL = e.target.result;

                fabric.Image.fromURL(imageURL, (image) => {
                    image.scaleToHeight(image.height * (scaleBySize[size] || 0.1));
                    image.scaleToWidth(image.width * (scaleBySize[size] || 0.1));
                    editor.canvas.add(image);
                    editor.canvas.setActiveObject(image);
                    setDisableSubmit(!editor?.canvas.getObjects().filter(item => item.id !== 'ruler').length);
                });
            }
        }
    };

    return (<Container maxWidth="md" sx={{px: {xs: 0}, display: 'flex', justifyContent: 'center', alignItem: 'center'}}>
        <IconButton onClick={() => (closeEditor && closeEditor())}
                    sx={{position: 'absolute', top: -5, right: -5, zIndex: 100}}>
            <ClearOutlined/>
        </IconButton>

        <Box style={{
            overflow: "hidden",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        }}>

            <CardContent sx={{ml: -1}}>
                <IconButton onClick={toggleRuler} sx={{color: (isRulerVisible ? 'secondary.main' : '')}}>
                    <StraightenOutlined/>
                </IconButton>
                <IconButton onClick={undo} disabled={undoHistory.length < 2}>
                    <Undo/>
                </IconButton>
                <IconButton onClick={redo} disabled={!redoHistory.length}>
                    <Redo/>
                </IconButton>
                <IconButton onClick={removeSelectedObject}
                            disabled={!editor?.canvas.getActiveObject()}>
                    <DeleteOutline/>
                </IconButton>
                <IconButton onClick={clear} disabled={!editor?.canvas.getObjects().length}>
                    <ClearOutlined/>
                </IconButton>
                <Button onClick={() => (closeEditor && closeEditor(exportSVG()))} variant={"contained"} sx={{ml: 2}}
                        disabled={disableSubmit}>
                    Submit
                </Button>
            </CardContent>


            <Paper sx={{width: "351px", height: "496px"}} square elevation={2}>
                <FabricJSCanvas className="sample-canvas" onReady={onReady}/>
            </Paper>

            <CardContent>
                <FileUploader
                    classes={"file-uploader"}
                    handleChange={onAddImage}
                    name="file"
                    types={fileTypes}
                    label={t('photoEditor.fileUpload.label')}
                    uploadedLabel={t('photoEditor.fileUpload.uploadedLabel')}
                    hoverTitle={t('photoEditor.fileUpload.hoverTitle')}
                />
            </CardContent>
        </Box>
    </Container>);
}

PhotoEditor.prototype = {
    closeEditor: PropTypes.func,
    fileTypes: PropTypes.arrayOf(PropTypes.string)
};
